import { Suspense, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography, Box } from "@mui/material";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

import { ButtonLoading } from "src/components/ButtonLoading";
import { FleetConfigTable } from "./FleetConfigTable";
import { useFleetConfigUpdateMutation } from "src/hooks";
import { FleetConfig as IFleetConfig } from "src/types";
import { ServerErrors } from "src/components/form-components/ServerErrors";
import { useScenario } from "src/hooks/useScenario";
import { isDisabled } from "src/utils/permission.utils";
import { IsDisabled, IsEnabled } from "src/components/permissions";

export function FleetConfigDialog(props: {
  scenarioId: number | string;
  lezId: number | string;
  open: boolean;
  onClose: () => void;
}) {
  const { data: scenario } = useScenario(props.scenarioId);

  const { t } = useTranslation();
  // dirty data is non-persisted fleet config
  const [dirtyData, setDirtyData] = useState<IFleetConfig | null>(null);

  const updateMutation = useFleetConfigUpdateMutation(props.lezId);

  /**
   * Functions
   */

  function handleChange(config: IFleetConfig) {
    setDirtyData(config);
  }

  function handleSave() {
    if (!dirtyData) return;
    updateMutation.mutateAsync(dirtyData).then(() => {
      props.onClose();
    });
  }

  // render the component
  return render();

  function render() {
    if (!scenario) return null;

    return (
      <StyledDialog fullWidth maxWidth="md" open={props.open}>
        <DialogContent>
          <Typography variant="h2" gutterBottom>
            {t("label.vehicleRestrictions")}
          </Typography>
          <Box mb={3}>
            <Typography>{t("label.vehicleRestrictionsInstructions")}</Typography>
          </Box>

          <Suspense fallback={<FleetConfigLoading />}>
            <FleetConfigTable
              className="fleetConfig"
              lezId={props.lezId}
              onChange={handleChange}
              readOnly={isDisabled(scenario.permissions.updateLEZ)}
            />
          </Suspense>
        </DialogContent>

        <DialogActions>
          {updateMutation.error?.response?.data && <ServerErrors data={updateMutation.error.response.data} />}

          <IsDisabled permission={scenario.permissions.updateLEZ}>
            <Button onClick={() => props.onClose()} color="primary">
              {t("label.close")}
            </Button>
          </IsDisabled>
          <IsEnabled permission={scenario.permissions.updateLEZ}>
            <Button onClick={() => props.onClose()} color="primary">
              {t("label.cancel")}
            </Button>

            <ButtonLoading disabled={dirtyData === null} isLoading={updateMutation.isLoading} onClick={handleSave}>
              {t("label.save")}
            </ButtonLoading>
          </IsEnabled>
        </DialogActions>
      </StyledDialog>
    );
  }
}

const StyledDialog = styled(Dialog)`
  .MuiDialogActions-root {
    padding: ${({ theme }) => theme.spacing(3)};
  }

  .MuiDialogContent-root {
    display: flex;
    flex-direction: column;
    overflow: auto;
    height: 100vh;
    max-height: 900px;
  }
  .fleetConfig {
    height: 0;
    flex: 1 1 100px;
  }
`;

const FleetConfigLoading = styled.div`
  height: 100vh;
`;
